import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FormManager, PageTitle, useCacheRecord } from 'gossamer-front-end';
import { Location, LocationRow } from 'cb-model';
import { getListRowLink } from 'gossamer-unified';
import { Button, Alert } from 'react-bootstrap';
import { LocationPickerModal } from './LocationPickerModal';
import { getGoogleMapsApiKey } from './googleMapsConfig';
import { useFormikContext } from 'formik';

export const LocationSetCoordinates = (): React.JSX.Element => {
  const params = useParams();
  const navigate = useNavigate();
  const record = useCacheRecord(Location, params.key);
  const handleExit = () => navigate(getListRowLink(Location.listRowLink, params.key));

  return (
    <>
      <h2>Set Coordinates - {record?.data?.name || 'Location'}</h2>
      <FormManager
        compoundUrl="/main/~/location-set-coordinates"
        handleCancel={handleExit}
        handleSuccess={handleExit}
        initialization={{
          main: { type: 'update-single', entity: Location, record },
        }}
      >
        <CoordinatesPicker locationName={record?.data?.name} />
      </FormManager>
    </>
  );
};

interface CoordinatesPickerProps {
  locationName?: string;
}

const CoordinatesPicker: React.FC<CoordinatesPickerProps> = ({ locationName }) => {
  const [showMapModal, setShowMapModal] = React.useState(false);
  const { values, setFieldValue } = useFormikContext<{ main: LocationRow }>();
  const [hasChanges, setHasChanges] = React.useState(false);

  const currentLat = values.main?.latitude;
  const currentLng = values.main?.longitude;
  const hasCoordinates = currentLat !== null && currentLng !== null;

  const handleLocationSelected = (lat: number, lng: number) => {
    setFieldValue('main.latitude', lat);
    setFieldValue('main.longitude', lng);
    setHasChanges(true);
  };

  const handleOpenMap = () => {
    setShowMapModal(true);
  };

  return (
    <div>
      <Alert variant="info">
        <Alert.Heading>Set Location Coordinates</Alert.Heading>
        <p>
          Use the map picker to select the precise geographic coordinates for{' '}
          <strong>{locationName || 'this location'}</strong>.
        </p>
        <ul>
          <li>Click anywhere on the map to place the marker</li>
          <li>Drag the marker to fine-tune the position</li>
          <li>Search for an address or place name</li>
        </ul>
      </Alert>

      {hasCoordinates && (
        <div className="mb-3">
          <h5>Current Coordinates</h5>
          <p className="mb-0">
            <strong>Latitude:</strong> {currentLat?.toFixed(6) || 'Not set'}
            <br />
            <strong>Longitude:</strong> {currentLng?.toFixed(6) || 'Not set'}
          </p>
        </div>
      )}

      {hasChanges && (
        <Alert variant="success">
          <strong>New coordinates selected!</strong> Click "Save" below to apply the changes.
        </Alert>
      )}

      <div className="mb-3">
        <Button variant="primary" size="sm" onClick={handleOpenMap}>
          📍 {hasCoordinates ? 'Update' : 'Set'} Location on Map
        </Button>
      </div>

      <LocationPickerModal
        show={showMapModal}
        onHide={() => setShowMapModal(false)}
        onLocationSelected={handleLocationSelected}
        apiKey={getGoogleMapsApiKey()}
        initialLat={currentLat || 51.5074}
        initialLng={currentLng || -0.1278}
        title={`Set Coordinates for ${locationName || 'Location'}`}
      />
    </div>
  );
};
